import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PageHeader from '../../components/PageHeader.jsx';
import SectionCard from '../../components/SectionCard.jsx';
import LifecycleNotice from '../../components/LifecycleNotice.jsx';
import KpiCard, { KpiGrid } from '../../components/KpiCard.jsx';
import { RunNowButton, RunNowStatus, canOfferRunNow, useRunNow } from '../../components/RunNow.jsx';
import { useScoreHistory } from '../../lib/use-score-history.js';
import { useRunPages } from '../../lib/use-run-pages.js';
import { localDayKey, daysBetween } from '../../lib/score-history.js';
import { customerBucketName } from '../../lib/customer-format.js';
import { dataSourceStates, sourceStatuses } from '../../lib/data-source-states.js';
import {
  collectionDays, collectionDaysCaption, collectionTone, collectionWord, durationText, runRows,
} from '../../lib/collection-status.js';
import { ALL_COLLECTOR_IDS, COLLECTOR_CATEGORIES } from '../../../../shared/collectors.mjs';
import { useCollectionSettings } from './use-collection-settings.js';
import HistoryTable, { whenParts } from './HistoryTable.jsx';
import SourcesCard, { NewSourcesNotice, SOURCES_ID } from './SourcesCard.jsx';
import ConsentNeeded, { missingFor, sourcesNeedingConsent } from './ConsentNeeded.jsx';
import ScheduleCard from './ScheduleCard.jsx';

/**
 * Data collection (ENTRAID-347 redesign, mockup 3, implementation plan C5): Sources, Collections and the schedule on
 * one page. Top to bottom:
 *   the page header with Collect now as its one action (the same eligibility, canOfferRunNow, and the same server
 *   checks as before), and the run state in words under it;
 *   the lifecycle notice and the new-sources notice;
 *   3 KPI cards: Last collection, Collections over the last 30 days (one tile per day), Active sources;
 *   History (every collection, partial sources named in the row, the row expands to every source);
 *   Sources (a switch per source, license notes inline, the sources that need a license or a permission);
 *   Schedule and retention, with the S3 data location.
 * One form covers Sources and Schedule, so the unsaved-changes guard spans the page. Who may open it is unchanged
 * (operator or the customer's admin); the API enforces it either way.
 */

const SUBLINE = 'What onGuard reads from Microsoft Entra ID and Microsoft 365, and how each collection went.';

const byNewest = (a, b) => (Date.parse(b?.runTimestamp ?? '') || 0) - (Date.parse(a?.runTimestamp ?? '') || 0);

function scrollToSources() {
  const el = typeof document !== 'undefined' ? document.getElementById(SOURCES_ID) : null;
  el?.scrollIntoView?.({ block: 'start' });
  el?.focus?.({ preventScroll: true });
}

/**
 * @param {{ customer: object, config?: object|null, recentRuns?: object[], audience?: string, onRefresh?: () => void,
 *   focusSources?: boolean }} props
 * `focusSources` scrolls to Sources on arrival (the old Sources URL).
 */
export default function DataCollectionPage({ customer, config = null, recentRuns = [], audience, onRefresh, focusSources = false }) {
  const customerId = customer?.customerId;
  const [reloadKey, setReloadKey] = useState(0);
  // The 30-day tiles read the light summary window (D1); the History table reads whole rows, a page at a time.
  const window30 = useScoreHistory(customerId, { enabled: Boolean(customerId), reloadKey });
  const history = useRunPages(customerId, { enabled: Boolean(customerId), reloadKey });
  const reload = useCallback(() => { setReloadKey((n) => n + 1); onRefresh?.(); }, [onRefresh]);
  const run = useRunNow(customer, reload, { enabled: canOfferRunNow(customer) });
  const form = useCollectionSettings({ customerId, tier: customer?.tierName ?? 'starter', config, onSave: onRefresh });
  const location = useLocation();

  // The record's latest collections show at once; the history pages join them as they arrive.
  const runs = useMemo(() => {
    const seen = new Map();
    for (const r of [...(recentRuns ?? []), ...(history.status === 'ready' ? history.runs : [])]) seen.set(String(r?.runTimestamp), r);
    return [...seen.values()].sort(byNewest);
  }, [history.status, history.runs, recentRuns]);
  const lastRun = runs[0] ?? null;
  const { unavailable, turnedOff } = dataSourceStates(config, lastRun);
  // ENTRAID-350: sources saved on but waiting for the admin to consent to their new permissions.
  const needsConsent = sourcesNeedingConsent(config?.enabledCollectors ?? [], customer?.entraAppClientId ? missingFor(customer) : []);
  const waitingSources = COLLECTOR_CATEGORIES.flatMap((c) => c.collectors).filter((c) => needsConsent.has(c.id));

  useEffect(() => {
    if (focusSources || location.hash === `#${SOURCES_ID}`) scrollToSources();
  }, [focusSources, location.hash]);

  const offer = canOfferRunNow(customer);
  return (
    <div>
      <PageHeader title="Data collection" subline={SUBLINE} action={offer ? <RunNowButton run={run} /> : null} />
      <div className="space-y-4">
        {offer ? <RunNowStatus run={run} /> : null}
        {/* A failed history read is not "no collection yet": the lifecycle notice waits for an answer. */}
        {customer ? <LifecycleNotice customer={customer} audience={audience} hasData={runs.length > 0 || history.status === 'error'} onRefresh={reload} /> : null}
        {customer ? <ConsentNeeded customer={customer} waitingSources={waitingSources} onRefresh={reload} /> : null}
        <NewSourcesNotice form={form} />
        {lastRun ? <CollectionKpis runs={runs} windowRuns={window30.status === 'ready' ? window30.runs : runs} lastRun={lastRun} config={config} unavailable={unavailable} turnedOff={turnedOff} /> : null}
        <SectionCard title="History" description="Kept for 13 months" testId="history-card">
          <HistoryTable
            runs={runs}
            loading={history.status === 'loading' && runs.length === 0}
            error={history.status === 'error' && runs.length === 0 ? history.error : null}
            onRetry={() => setReloadKey((n) => n + 1)}
            hasMore={history.hasMore}
            onLoadMore={history.loadMore}
            loadingMore={history.loadingMore}
          />
          {history.status === 'ready' && history.error ? <p className="pt-3 text-[13px] text-ipc-muted" role="status">{history.error}</p> : null}
          {history.status === 'error' && runs.length > 0 ? (
            <p className="pt-3 text-[13px] text-ipc-muted" role="status">
              Showing the latest collections only: the full history could not be loaded.{' '}
              <button type="button" className="tap font-semibold text-ipc-light hover:underline" onClick={() => setReloadKey((n) => n + 1)}>Try again</button>
            </p>
          ) : null}
        </SectionCard>
        <SourcesCard customerId={customerId} form={form} unavailable={unavailable} needsConsent={needsConsent} />
        <ScheduleCard form={form} bucketName={customerBucketName(customer)} />
      </div>
    </div>
  );
}

function CollectionKpis({ runs, windowRuns, lastRun, config, unavailable, turnedOff }) {
  const when = whenParts(lastRun.runTimestamp);
  const duration = durationText(lastRun.durationMs);
  const rows = runRows(lastRun).toLocaleString('en-US');
  // Today shows the time as the value; an older collection shows its day as the value and the time in the caption.
  const lastCaption = `${collectionWord(lastRun.statusCode)}${when.day === 'Today' ? '' : ` at ${when.time}`}, ${rows} rows${duration ? ` in ${duration}` : ''}`;
  // A collection older than the window means the customer is older than it: every day of the 30 counts.
  const today = localDayKey(Date.now());
  const fullWindow = runs.some((r) => { const k = localDayKey(r?.runTimestamp); return k && daysBetween(k, today) >= 30; });
  const grid = collectionDays([...windowRuns, ...runs], { fullWindow });
  const daysCaption = collectionDaysCaption(grid);
  const active = (config?.enabledCollectors ?? []).filter((id) => ALL_COLLECTOR_IDS.includes(id)).length;
  const total = ALL_COLLECTOR_IDS.length;
  const sourcesCaption = unavailable.length > 0
    ? `${unavailable.length} ${unavailable.length === 1 ? 'needs' : 'need'} a license or a permission`
    : turnedOff.length > 0 ? `${turnedOff.length} turned off` : 'Every source is on';
  // The same per-source squares as the Overview "Collections today" card (ENTRAID-347).
  const sources = sourceStatuses(config, lastRun);
  return (
    <KpiGrid max={3} data-testid="collection-kpis">
      <KpiCard
        label="Last collection"
        value={when.day === 'Today' ? when.time : when.day}
        tone={collectionTone(lastRun.statusCode)}
        caption={lastCaption}
        variant="tiles"
        tileVariant="status"
        tileStates={sources.map((s) => s.status)}
        tileLabels={sources.map((s) => `${s.label}: ${s.status}`)}
      />
      <KpiCard
        label={`Collections, last ${grid.windowDays} ${grid.windowDays === 1 ? 'day' : 'days'}`}
        kpi="Collections, last 30 days"
        value={grid.complete}
        unit={`of ${grid.windowDays} complete`}
        variant="tiles"
        tileStates={grid.states}
        tone={daysCaption.tone}
        caption={daysCaption.text}
      />
      <KpiCard
        label="Active sources"
        value={active}
        unit={`of ${total}`}
        variant="tiles"
        filled={active}
        total={total}
        tone={unavailable.length > 0 ? 'warn' : undefined}
        caption={sourcesCaption}
      />
    </KpiGrid>
  );
}
